import { Chart as ChartJS, registerables } from "chart.js";
import type { TooltipItem } from "chart.js";
import useDashboardApi from "../hooks/useDashboardApi";
import { DATE_FORMAT_API } from "config/settings";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { CategoryScale } from "chart.js";
import { Bar } from "react-chartjs-2";
import { useEffect, useState } from "react";
import { Card, Col, Empty, Spin } from "antd";
import dayjs from "dayjs";
import { FaChartBar } from "react-icons/fa";
import CardTitle from "components/CardTitle";
import useCoreStore from "views/hooks/useCoreStore";

ChartJS.register(CategoryScale);
ChartJS.register(...registerables);

interface BarChartPropsType {
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
}

export default function BarChart(props: BarChartPropsType) {
  const { startDate, endDate } = props;
  const { t } = useTranslation();
  const [chartHeight, setChartHeight] = useState(300);

  const currency = useCoreStore(state => state.currency);
  const currencyCode = currency?.code;

  const {
    listUserSells,
    toplistUsersData: data,
    loadingUserSellsClients: loading,
  } = useDashboardApi();
  const urlParams: RouteParams = useParams();

  useEffect(() => {
    if (urlParams?.companyId) {
      listUserSells({
        variables: {
          filter: {
            companyId: urlParams?.companyId,
            endDate: endDate && dayjs(endDate).format(DATE_FORMAT_API),
            startDate: startDate && dayjs(startDate).format(DATE_FORMAT_API),
          },
        },
      });
    }

    const updateChartHeight = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setChartHeight(250);
      } else {
        setChartHeight(300);
      }
    };

    updateChartHeight();
    window.addEventListener("resize", updateChartHeight);

    return () => window.removeEventListener("resize", updateChartHeight);
  }, [urlParams?.companyId, endDate, startDate]);

  const options = {
    indexAxis: "y" as const,
    elements: {
      bar: {
        borderWidth: 1,
        borderRadius: 4,
        borderColor: "#fff",
      },
    },
    responsive: true,
    plugins: {
      legend: {
        position: "right" as const,
        labels: {
          boxWidth: 15,
          padding: 15,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleFont: {
          size: 13,
        },
        bodyFont: {
          size: 12,
        },
        padding: 10,
        cornerRadius: 6,
        callbacks: {
          label: function (context: TooltipItem<"bar">) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.x !== null) {
              label += new Intl.NumberFormat("fr-FR").format(context.parsed.x) + " " + currencyCode;
            }
            return label;
          },
        },
      },
    },
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
        },
        ticks: {
          callback: function (value: string | number) {
            return new Intl.NumberFormat("fr-FR").format(Number(value)) + " " + currencyCode;
          },
        },
      },
      y: {
        grid: {
          display: false,
        },
      },
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10,
        left: 10,
        right: 20,
      },
    },
  };

  const renderChart = () => {
    if (loading) {
      return (
        <div className="chart-loading-container">
          <Spin size="large" />
        </div>
      );
    }

    if (!data || Object.keys(data).length === 0) {
      return (
        <div className="chart-empty-container">
          <Empty description={t("No data available")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      );
    }

    return <Bar height={chartHeight} data={data} options={options} />;
  };

  return (
    <Col md={12} sm={24} xs={24}>
      <Card
        className="antd-newrai-card line-chart-card"
        title={<CardTitle icon={<FaChartBar />} title={t("Production before tax per user")} />}
      >
        {renderChart()}
      </Card>
    </Col>
  );
}
